/**
 * Pricing page - Brutalist minimalism
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';

export const Pricing: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'annual' | 'monthly'>('annual');
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionType, setSubscriptionType] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      apiClient.getUserProfile()
        .then(profile => {
          setSubscriptionStatus(profile.subscription_status);
          setSubscriptionType(profile.subscription_type);
          // Set billing cycle toggle to match user's current plan
          if (profile.subscription_type === 'monthly') {
            setBillingCycle('monthly');
          } else if (profile.subscription_type === 'annual') {
            setBillingCycle('annual');
          }
        })
        .catch(() => {
          setSubscriptionStatus(null);
          setSubscriptionType(null);
        });
    }
  }, [user]);

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/signin');
      return;
    }

    setLoading(true);
    try {
      const successUrl = `${window.location.origin}/dashboard?checkout=success`;
      const cancelUrl = `${window.location.origin}/pricing?checkout=cancel`;

      const { checkout_url } = await apiClient.createCheckoutSession(billingCycle, successUrl, cancelUrl);
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const handleDowngrade = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { portal_url } = await apiClient.createPortalSession(window.location.href);
      window.location.href = portal_url;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      alert('Failed to open billing portal. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-24">
      {/* Header */}
      <div className="mb-12 md:mb-24 text-center">
        <h1 className="text-3xl md:text-5xl font-bold mb-6 md:mb-8">Pricing</h1>

        {/* Billing Cycle Toggle */}
        <div className="flex items-center justify-center gap-2 p-1 bg-black border border-border-dim rounded w-fit mx-auto">
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-4 md:px-6 py-2 text-xs md:text-sm font-mono transition-colors ${
              billingCycle === 'annual'
                ? 'bg-white text-black'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            ANNUAL
          </button>
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 md:px-6 py-2 text-xs md:text-sm font-mono transition-colors ${
              billingCycle === 'monthly'
                ? 'bg-white text-black'
                : 'text-text-secondary hover:text-white'
            }`}
          >
            MONTHLY
          </button>
        </div>
      </div>

      {/* Pricing Grid - Brutalist Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-24">
        {/* Free Tier */}
        <div className={`card ${subscriptionStatus === 'free' || (user && !subscriptionStatus) ? 'border-white' : ''}`}>
          <div className="mb-8">
            <div className="text-xs font-mono text-text-muted mb-2">TIER 01</div>
            <h3 className="text-2xl font-bold mb-4">Free</h3>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-5xl font-bold font-mono">$0</span>
            </div>
            <div className="text-text-muted text-sm">Single matrix evaluation</div>
          </div>

          <div className="space-y-3 mb-8 text-sm">
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>1 permitting matrix</div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>All project types</div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>Standard support</div>
            </div>
          </div>

          {subscriptionStatus === 'free' || (user && !subscriptionStatus) ? (
            <div className="w-full py-3 text-center text-sm font-mono text-text-secondary border border-border-dim rounded">
              SELECTED
            </div>
          ) : subscriptionStatus === 'active' || subscriptionStatus === 'canceled' ? (
            <button
              onClick={handleDowngrade}
              disabled={loading}
              className="btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Downgrade'}
            </button>
          ) : (
            <button
              onClick={() => navigate('/signup')}
              className="btn-secondary w-full"
            >
              Get Started
            </button>
          )}
        </div>

        {/* Pro Tier */}
        <div className={`card ${(subscriptionStatus === 'active' || subscriptionStatus === 'canceled') && subscriptionType === billingCycle ? 'border-white' : ''}`}>
          <div className="mb-8">
            <div className={`text-xs font-mono mb-2 ${(subscriptionStatus === 'active' || subscriptionStatus === 'canceled') && subscriptionType === billingCycle ? 'text-white' : 'text-text-muted'}`}>TIER 02</div>
            <h3 className="text-2xl font-bold mb-4">Pro</h3>

            {/* Dynamic Pricing */}
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-5xl font-bold font-mono">
                ${billingCycle === 'annual' ? '1,080' : '100'}
              </span>
              <span className="text-text-secondary">
                /{billingCycle === 'annual' ? 'year' : 'mo'}
              </span>
            </div>
            {billingCycle === 'annual' && (
              <div className="text-text-muted text-sm mb-4">
                $90/month billed annually (10% savings)
              </div>
            )}
          </div>

          <div className="space-y-3 mb-8 text-sm">
            <div className="text-text-muted mb-3">Everything in Free, plus:</div>
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>5 matrices per month</div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>Priority support</div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>Export capabilities</div>
            </div>
          </div>

          {(subscriptionStatus === 'active' || subscriptionStatus === 'canceled') && subscriptionType === billingCycle ? (
            <div className="w-full py-3 text-center text-sm font-mono text-text-secondary border border-border-dim rounded">
              SELECTED
            </div>
          ) : (subscriptionStatus === 'active' || subscriptionStatus === 'canceled') && subscriptionType !== billingCycle ? (
            <button
              onClick={handleDowngrade}
              disabled={loading}
              className="btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Change Plan'}
            </button>
          ) : (
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <img src="/logo-primary.svg" alt="" className="h-4 w-4 ring-pulse inline" />
                  Redirecting to checkout...
                </span>
              ) : (
                `Subscribe ${billingCycle === 'annual' ? 'Annually' : 'Monthly'}`
              )}
            </button>
          )}
        </div>

        {/* Enterprise Tier */}
        <div className="card">
          <div className="mb-8">
            <div className="text-xs font-mono text-text-muted mb-2">TIER 03</div>
            <h3 className="text-2xl font-bold mb-4">Enterprise</h3>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-bold font-mono">Custom</span>
            </div>
            <div className="text-text-muted text-sm">Multi-seat deployment</div>
          </div>

          <div className="space-y-3 mb-8 text-sm">
            <div className="text-text-muted mb-3">Everything in Pro, plus:</div>
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>Custom matrix quotas</div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>Multiple seats</div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>Team management</div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>Dedicated support</div>
            </div>
            <div className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-2"></div>
              <div>Custom integrations</div>
            </div>
          </div>

          <a
            href="mailto:sales@permitloop.ai"
            className="btn-secondary w-full block text-center"
          >
            Contact Sales
          </a>
        </div>
      </div>

      {/* FAQ Section - Minimal */}
      <div className="border-t border-border-dim pt-12 md:pt-24">
        <h2 className="text-xl md:text-2xl font-bold mb-8 md:mb-12">FAQ</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-x-16 md:gap-y-8 max-w-5xl">
          <div>
            <h4 className="font-bold mb-2">Can I cancel anytime?</h4>
            <p className="text-text-secondary text-sm">
              Yes. Cancel from your dashboard. No questions asked.
            </p>
          </div>
          <div>
            <h4 className="font-bold mb-2">What happens to my data?</h4>
            <p className="text-text-secondary text-sm">
              Matrices remain accessible post-subscription cancellation.
            </p>
          </div>
          <div>
            <h4 className="font-bold mb-2">Enterprise custom pricing?</h4>
            <p className="text-text-secondary text-sm">
              Contact sales@permitloop.ai for volume pricing and custom terms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
